/**
 * Protocole client/serveur (module pur, teste).
 *
 * JSON pour tout le controle, binaire uniquement pour les dessins.
 *
 * Sens client -> serveur : une trame binaire EST un dessin, sans enveloppe. Le
 * contexte (phase courante, expediteur) suffit au serveur pour l'attribuer.
 *
 * Sens serveur -> client : il faut pouvoir adresser plusieurs dessins (une
 * cible de devinette, puis N dessins a la revelation), donc une enveloppe
 * minimale de 3 octets precede la charge utile :
 *
 *   uint8   tag    (DrawingTag)
 *   uint16  slot   (index annonce dans le message JSON qui precede)
 *   ...     dessin opaque, jamais decode par le serveur
 *
 * Le serveur se contente de prefixer : il ne lit toujours pas le contenu.
 */
import { ANY_DRAW_TIME_MIN, DRAW_TIME_MAX, GUESS_TIME_MAX, GUESS_TIME_MIN, isInRange, MAX_PLAYERS, MIN_PLAYERS, NICKNAME_MAX_LENGTH, NICKNAME_MIN_LENGTH, ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH, ROUNDS_MAX, ROUNDS_MIN, } from './constants.js';
import { GAME_MODES } from './types.js';
import { isAvatarId } from './avatars.js';
import { isValidCategorySelection } from './categories.js';
import { isZoneSize } from './zone.js';
export const ROOM_VISIBILITIES = ['public', 'private'];
export const PHASES = [
    'LOBBY',
    'COUNTDOWN',
    'DRAWING',
    'COLLECTING',
    'GUESSING',
    'REVEAL',
    'GAME_END',
];
// ---------------------------------------------------------------------------
// Enveloppe binaire (serveur -> client)
// ---------------------------------------------------------------------------
/**
 * `collabBase` : les retouches DEJA posees sur le tableau qu'on vient de
 * recevoir, une trame par auteur, dans l'ordre ou elles ont ete faites. Le mode
 * collaboratif est le seul ou l'on dessine PAR-DESSUS le travail d'un autre :
 * sans elles, chacun repartirait d'une zone vierge et le tableau ne se
 * construirait jamais.
 *
 * `collabBoard` : tout le contenu du vote, N tableaux fois N auteurs. Le slot y
 * porte les deux indices a la fois (cf. `collabSlot`), l'enveloppe binaire n'en
 * ayant qu'un.
 */
export const DRAWING_TAG = {
    guessTarget: 1,
    reveal: 2,
    gallery: 3,
    hunt: 4,
    preview: 5,
    collabBase: 6,
    collabBoard: 7,
};
const DRAWING_TAGS = Object.values(DRAWING_TAG);
/**
 * Mode collaboratif : le couple (tableau, auteur) tient dans l'unique slot de
 * l'enveloppe binaire.
 *
 * L'enveloppe ne porte qu'un entier, et le vote a besoin de deux indices. On
 * les empile sur la base MAX_PLAYERS, ce qui est sur : les deux sont des
 * indices de joueur, donc bornes par ce meme nombre, et le produit tient
 * largement dans l'uint16 de l'enveloppe.
 */
export function collabSlot(board, author) {
    return board * MAX_PLAYERS + author;
}
export function collabBoardOf(slot) {
    return Math.floor(slot / MAX_PLAYERS);
}
export function collabAuthorOf(slot) {
    return slot % MAX_PLAYERS;
}
export const DRAWING_ENVELOPE_BYTES = 3;
export function encodeDrawingFrame(tag, slot, drawing) {
    const out = new ArrayBuffer(DRAWING_ENVELOPE_BYTES + drawing.byteLength);
    const view = new DataView(out);
    view.setUint8(0, tag);
    view.setUint16(1, slot, true);
    new Uint8Array(out, DRAWING_ENVELOPE_BYTES).set(new Uint8Array(drawing));
    return out;
}
// ---------------------------------------------------------------------------
// Enveloppe binaire (client -> serveur)
// ---------------------------------------------------------------------------
/**
 * Un octet de tete, et un seul, dans le sens montant.
 *
 * Il n'y en avait pas : une trame binaire ETAIT le dessin rendu. Le mode
 * spectateur ajoute un second usage au meme format, l'apercu en cours de
 * dessin, et les deux ne peuvent pas se distinguer par leur contenu : ce sont
 * exactement les memes traits, l'un fige et l'autre non. Sans ce tag, le
 * premier apercu vaudrait validation et le joueur perdrait sa manche.
 *
 *  - `submit`  : le dessin rendu. Definitif, il fait avancer la manche.
 *  - `preview` : l'etat courant, relaye aux seuls spectateurs qui regardent
 *                cet auteur. Le serveur ne le decode pas davantage, il ne le
 *                garde que jusqu'a la fin de la manche, et il n'entre dans
 *                aucun score.
 */
export const CLIENT_DRAWING_TAG = { submit: 1, preview: 2 };
const CLIENT_DRAWING_TAGS = Object.values(CLIENT_DRAWING_TAG);
/**
 * Un octet de tag, puis la MANCHE sur deux octets.
 *
 * La manche n'est pas une precaution theorique. Sans elle, un dessin parti
 * juste avant la fin d'une manche et arrive juste apres etait accepte pour la
 * manche SUIVANTE : son auteur voyait son nouveau tableau, son envoi partait
 * ensuite en « deja rendu », et il perdait purement et simplement son tour. En
 * collaboratif, ou la manche suivante porte sur un AUTRE tableau, sa retouche
 * atterrissait en plus sur le mauvais.
 *
 * Le cas se produit exactement quand tous les clients rendent en meme temps,
 * c'est-a-dire a l'expiration du chrono : il suffit qu'un envoi soit un peu
 * plus lent que le delai de collecte.
 */
export const CLIENT_ENVELOPE_BYTES = 3;
export function encodeClientDrawing(tag, drawing, round) {
    const out = new ArrayBuffer(CLIENT_ENVELOPE_BYTES + drawing.byteLength);
    const view = new DataView(out);
    view.setUint8(0, tag);
    view.setUint16(1, Math.max(Math.min(Math.round(round), 65535), 0), true);
    new Uint8Array(out, CLIENT_ENVELOPE_BYTES).set(new Uint8Array(drawing));
    return out;
}
export function decodeClientDrawing(buffer) {
    if (buffer.byteLength < CLIENT_ENVELOPE_BYTES)
        return null;
    const view = new DataView(buffer);
    const tag = view.getUint8(0);
    if (!CLIENT_DRAWING_TAGS.includes(tag))
        return null;
    return {
        tag: tag,
        round: view.getUint16(1, true),
        drawing: buffer.slice(CLIENT_ENVELOPE_BYTES),
    };
}
export function decodeDrawingFrame(buffer) {
    if (buffer.byteLength < DRAWING_ENVELOPE_BYTES)
        return null;
    const view = new DataView(buffer);
    const tag = view.getUint8(0);
    if (!DRAWING_TAGS.includes(tag))
        return null;
    return {
        tag: tag,
        slot: view.getUint16(1, true),
        drawing: buffer.slice(DRAWING_ENVELOPE_BYTES),
    };
}
// ---------------------------------------------------------------------------
// Validation des entrees
// ---------------------------------------------------------------------------
/**
 * Controles C0/C1, espaces de largeur nulle, marques et surcharges bidi,
 * separateurs de ligne, joiners et BOM. Les marques bidi permettent de faire
 * passer un pseudo pour un autre a l'affichage : elles n'ont aucune raison
 * d'etre dans un pseudo de jeu.
 */
const CONTROL_AND_BIDI = /[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u2028-\u202E\u2060-\u206F\uFEFF]/g;
/**
 * Nettoie un pseudo : trim, retrait des caracteres de controle et de
 * direction, longueur bornee. Retourne null si le resultat est inexploitable.
 */
export function sanitizeNickname(raw) {
    if (typeof raw !== 'string')
        return null;
    const stripped = raw
        .replace(CONTROL_AND_BIDI, '')
        .trim()
        .replace(/\s+/g, ' ');
    const characters = [...stripped];
    if (characters.length < NICKNAME_MIN_LENGTH)
        return null;
    return characters.slice(0, NICKNAME_MAX_LENGTH).join('');
}
export function isValidRoomCode(raw) {
    if (typeof raw !== 'string' || raw.length !== ROOM_CODE_LENGTH)
        return false;
    return [...raw].every((character) => ROOM_CODE_ALPHABET.includes(character));
}
export function normalizeRoomCode(raw) {
    return raw.trim().toUpperCase();
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isNormalizedCoordinate(value) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;
}
/** Longueur max d'un message de chat. */
export const CHAT_MAX_LENGTH = 200;
/**
 * Nettoie un message de chat : memes retraits que pour un pseudo (controles,
 * marques bidi), espaces compactes, longueur bornee. Retourne null si le
 * message est vide apres nettoyage.
 *
 * Le rendu est fait par React, donc echappe : aucun risque d'injection. Ce
 * nettoyage sert a empecher les messages qui deforment la mise en page.
 */
export function sanitizeChat(raw) {
    if (typeof raw !== 'string')
        return null;
    const cleaned = raw.replace(CONTROL_AND_BIDI, '').replace(/\s+/g, ' ').trim();
    if (cleaned.length === 0)
        return null;
    return [...cleaned].slice(0, CHAT_MAX_LENGTH).join('');
}
/** `colorIndex` est un uint8 : la palette ne peut pas depasser 256 entrees. */
export const MAX_PALETTE_COLORS = 256;
/** Hexadecimal strict : ces valeurs finissent dans un fillStyle. */
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
/** Reactions autorisees. Toute autre valeur est rejetee. */
export const REACTIONS = ['👏', '😂', '😮', '🔥', '💀'];
/**
 * Parse et valide un message de controle. Retourne null si le message est
 * malforme : le serveur ne doit jamais faire confiance a ce qui arrive.
 */
export function parseClientMessage(raw) {
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (!isRecord(parsed) || typeof parsed['type'] !== 'string')
        return null;
    switch (parsed['type']) {
        case 'join': {
            const nickname = sanitizeNickname(parsed['nickname']);
            if (nickname === null)
                return null;
            const message = { type: 'join', nickname };
            // Forme uniquement : `famille_numero`, borne court. Un identifiant
            // inconnu du client est simplement ignore a l'affichage, il ne peut donc
            // rien casser ; ce controle evite surtout qu'une chaine arbitraire
            // circule dans les instantanes de tout le salon.
            const rawAvatar = parsed['avatar'];
            if (isAvatarId(rawAvatar))
                message.avatar = rawAvatar;
            const rawCode = parsed['code'];
            if (typeof rawCode === 'string' && rawCode.length > 0) {
                const code = normalizeRoomCode(rawCode);
                if (!isValidRoomCode(code))
                    return null;
                message.code = code;
            }
            if (parsed['create'] === true) {
                message.create = true;
                const visibility = parsed['visibility'];
                if (visibility !== undefined) {
                    if (!ROOM_VISIBILITIES.includes(visibility))
                        return null;
                    message.visibility = visibility;
                }
            }
            const token = parsed['sessionToken'];
            if (typeof token === 'string' && token.length > 0 && token.length <= 64) {
                message.sessionToken = token;
            }
            return message;
        }
        case 'settings': {
            const message = { type: 'settings' };
            const mode = parsed['mode'];
            if (mode !== undefined) {
                if (!GAME_MODES.includes(mode))
                    return null;
                message.mode = mode;
            }
            // Chaque reglage numerique est valide contre sa PLAGE, pas contre la
            // liste des raccourcis d'interface : les valeurs personnalisees sont
            // permises, mais bornees. Une valeur hors plage, fractionnaire ou non
            // numerique fait echouer tout le message, qui repart en ERROR. Le serveur
            // ne voit donc jamais une duree arbitraire.
            const rounds = parsed['rounds'];
            if (rounds !== undefined) {
                if (!isInRange(rounds, ROUNDS_MIN, ROUNDS_MAX))
                    return null;
                message.rounds = rounds;
            }
            // Les durees se bornent ici sur l'UNION des modes, et pas sur la plage
            // d'un mode en particulier : ce message peut changer le mode ET la duree
            // a la fois, le protocole ne sait pas encore lequel s'appliquera. La
            // regle fine est celle de la room, qui connait le mode vise.
            //
            // Le plancher est celui du plus court des modes : le collaboratif
            // descend a 10 s, et laisser 15 ici faisait rejeter un reglage que son
            // propre selecteur proposait.
            const drawTime = parsed['drawTime'];
            if (drawTime !== undefined) {
                if (!isInRange(drawTime, ANY_DRAW_TIME_MIN, DRAW_TIME_MAX))
                    return null;
                message.drawTime = drawTime;
            }
            const guessTime = parsed['guessTime'];
            if (guessTime !== undefined) {
                if (!isInRange(guessTime, GUESS_TIME_MIN, GUESS_TIME_MAX))
                    return null;
                message.guessTime = guessTime;
            }
            const maxPlayers = parsed['maxPlayers'];
            if (maxPlayers !== undefined) {
                if (!isInRange(maxPlayers, MIN_PLAYERS, MAX_PLAYERS))
                    return null;
                message.maxPlayers = maxPlayers;
            }
            // Forme uniquement : non vide, sans doublon, valeurs connues. Le vide
            // est refuse ICI, pas seulement grise dans l'interface : une partie sans
            // categorie n'aurait aucun tableau a tirer. L'EXISTENCE de contenu dans
            // chaque categorie est verifiee par le serveur, qui seul connait le
            // catalogue.
            const categories = parsed['categories'];
            if (categories !== undefined) {
                if (!isValidCategorySelection(categories))
                    return null;
                message.categories = [...categories].sort((a, b) => a - b);
            }
            const zoneSize = parsed['zoneSize'];
            if (zoneSize !== undefined) {
                if (!isZoneSize(zoneSize))
                    return null;
                message.zoneSize = zoneSize;
            }
            if (message.mode === undefined &&
                message.rounds === undefined &&
                message.drawTime === undefined &&
                message.guessTime === undefined &&
                message.maxPlayers === undefined &&
                message.categories === undefined &&
                message.zoneSize === undefined) {
                return null;
            }
            return message;
        }
        case 'start':
            return { type: 'start' };
        case 'palette': {
            const colors = parsed['colors'];
            if (!Array.isArray(colors) || colors.length === 0 || colors.length > MAX_PALETTE_COLORS) {
                return null;
            }
            // Chaque entree finit dans un ctx.fillStyle : on n'accepte qu'un
            // hexadecimal strict, jamais une chaine CSS arbitraire.
            if (!colors.every((color) => typeof color === 'string' && HEX_COLOR.test(color))) {
                return null;
            }
            return { type: 'palette', colors: colors.map((color) => color.toLowerCase()) };
        }
        case 'guess': {
            if (!isNormalizedCoordinate(parsed['x']) || !isNormalizedCoordinate(parsed['y']))
                return null;
            return { type: 'guess', x: parsed['x'], y: parsed['y'] };
        }
        case 'vote': {
            // Le slot est un indice de joueur dans le salon : borne par MAX_PLAYERS.
            const slot = parsed['slot'];
            if (typeof slot !== 'number' || !Number.isInteger(slot))
                return null;
            if (slot < 0 || slot >= MAX_PLAYERS)
                return null;
            // Mode collaboratif : on vote une fois PAR TABLEAU, il faut donc dire
            // lequel. Absent en concours, ou il n'y a qu'un tableau a juger.
            const board = parsed['board'];
            if (board === undefined)
                return { type: 'vote', slot };
            if (typeof board !== 'number' || !Number.isInteger(board))
                return null;
            if (board < 0 || board >= MAX_PLAYERS)
                return null;
            return { type: 'vote', slot, board };
        }
        case 'spectate': {
            const target = parsed['target'];
            if (target === null)
                return { type: 'spectate', target: null };
            // Un identifiant de joueur est un UUID : on borne, on ne valide pas la
            // forme. Le serveur ne connait de toute facon que ceux de son salon.
            if (typeof target !== 'string' || target.length === 0 || target.length > 64)
                return null;
            return { type: 'spectate', target };
        }
        case 'reaction': {
            const emoji = parsed['emoji'];
            if (!REACTIONS.includes(emoji))
                return null;
            return { type: 'reaction', emoji: emoji };
        }
        case 'playAgain':
            return { type: 'playAgain' };
        case 'listRooms':
            return { type: 'listRooms' };
        case 'chat': {
            const text = sanitizeChat(parsed['text']);
            if (text === null)
                return null;
            return { type: 'chat', text };
        }
        case 'leave':
            return { type: 'leave' };
        case 'ping': {
            const t = parsed['t'];
            if (typeof t !== 'number' || !Number.isFinite(t))
                return null;
            return { type: 'ping', t };
        }
        default:
            return null;
    }
}
export function serialize(message) {
    return JSON.stringify(message);
}
export function parseServerMessage(raw) {
    try {
        const parsed = JSON.parse(raw);
        if (!isRecord(parsed) || typeof parsed['type'] !== 'string')
            return null;
        return parsed;
    }
    catch {
        return null;
    }
}
// ---------------------------------------------------------------------------
// Regles de jeu partagees
// ---------------------------------------------------------------------------
/**
 * Index du dessinateur dont le joueur `playerIndex` doit deviner la retouche a
 * la manche `round` (1-indexee).
 *
 * COMPROMIS: le CLAUDE.md ecrit (i + r) mod n, mais cette formule renvoie le
 * joueur sur lui-meme des que r est un multiple de n : par exemple a 2 joueurs
 * a la manche 2. On replie donc le decalage sur [1, n-1], ce qui garantit la
 * regle enoncee ("jamais le sien") pour tout nombre de manches.
 */
export function guessTargetIndex(playerIndex, round, playerCount) {
    if (playerCount < 2)
        return playerIndex;
    const offset = ((round - 1) % (playerCount - 1)) + 1;
    return (playerIndex + offset) % playerCount;
}
export function canStart(playerCount) {
    return playerCount >= 2 && playerCount <= MAX_PLAYERS;
}
//# sourceMappingURL=protocol.js.map